import React, { useCallback, useState } from "react";
import { Field } from "@design-system/Field/Field";
import { PlainModal } from "@design-system/Modal/Modal";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    deriveDueDateFromTermSelection,
    NativeInvoiceCustomTermValue,
    NativeInvoiceTermType,
} from "@common/helpers/invoice-terms.helper";
import { NativeInvoiceCustomTermModal } from "./NativeInvoiceCustomTermModal";
import { NATIVE_INVOICE_TERM_OPTIONS } from "./nativeInvoiceTermsOptions";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";

// Terms dropdown (PRO-11287): On Receipt / Net 15 / Net 30 / Net 60 / Net 90 /
// Custom. Selecting a standard term immediately (re)computes `dueDate` from
// `invoiceDate` using calendar-day math (no weekend shifting). Selecting
// Custom opens a modal (Due in N days / Due by the Nth day of the current or
// next month); saving it computes the due date the same way. Manually
// overriding `dueDate` maps back onto the matching term (Net N, or Custom
// due-in-days).
export const NativeInvoiceTermsField: React.FC = () => {
    const { state, setState } = useNativeInvoiceFormContext();
    const [showCustomModal, setShowCustomModal] = useState(false);

    const applyTermType = useCallback(
        (
            termType: NativeInvoiceTermType,
            customTerm: NativeInvoiceCustomTermValue | null,
        ) => {
            setState((prev) => {
                const derivedDueDate = deriveDueDateFromTermSelection({
                    invoiceDate: prev.invoiceDate,
                    termType,
                    customTerm,
                });
                return {
                    ...prev,
                    termType,
                    customTerm,
                    dueDate: derivedDueDate ?? prev.dueDate,
                };
            });
        },
        [setState],
    );

    const handleValueChange = useCallback(
        (value: NativeInvoiceTermType | null) => {
            if (value === null) {
                return;
            }
            if (value === NativeInvoiceTermType.CUSTOM) {
                // Mirrors JournalEntryRecurrenceSelect's handleCustom: only
                // open the modal here. termType/customTerm/dueDate are only
                // committed on Save, so cancelling leaves the prior selection
                // (and dueDate) untouched instead of stranding termType at
                // "custom" with no rule behind it.
                setShowCustomModal(true);
                return;
            }
            applyTermType(value, null);
        },
        [applyTermType],
    );

    const handleSaveCustomTerm = useCallback(
        (customTerm: NativeInvoiceCustomTermValue) => {
            applyTermType(NativeInvoiceTermType.CUSTOM, customTerm);
            setShowCustomModal(false);
        },
        [applyTermType],
    );

    const handleCancelCustomTerm = useCallback(() => {
        setShowCustomModal(false);
    }, []);

    const isKnownTerm = NATIVE_INVOICE_TERM_OPTIONS.some(
        (option) => option.value === state.termType,
    );

    return (
        <>
            <Field label="Terms" control="button">
                <Picker.Select
                    items={NATIVE_INVOICE_TERM_OPTIONS}
                    searchable
                    searchPlaceholder="Search terms..."
                    value={isKnownTerm ? state.termType : null}
                    onValueChange={handleValueChange}
                    placeholder="Select"
                    trigger={<Picker.Button variant="primary" size="xl" />}
                />
            </Field>
            <PlainModal show={showCustomModal} onHide={handleCancelCustomTerm}>
                <NativeInvoiceCustomTermModal
                    initialValue={state.customTerm}
                    onSave={handleSaveCustomTerm}
                    onCancel={handleCancelCustomTerm}
                />
            </PlainModal>
        </>
    );
};
